import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { TenantConfig } from '../types/tenant';
import { loadTenantConfig } from '../lib/loadTenantConfig';
import { deriveHoverHex } from '../lib/color';
import {
  TenantConfigContext,
  type TenantConfigContextValue,
  type TenantStatus,
} from './tenant-context';

const DEFAULT_TENANT_ID = 'cruzfind';

function readInitialTenantId(): string {
  return DEFAULT_TENANT_ID;
}

function applyBrandingToDocument(config: TenantConfig) {
  const root = document.documentElement;
  const hover = config.branding.accentHoverHex ?? deriveHoverHex(config.branding.accentHex);
  root.style.setProperty('--brand-accent', config.branding.accentHex);
  root.style.setProperty('--brand-accent-hover', hover);
  root.style.setProperty('--brand-accent-foreground', config.branding.accentForegroundHex);
  document.title = config.displayName;
  const themeMeta = document.querySelector('meta[name="theme-color"]');
  themeMeta?.setAttribute('content', config.branding.accentHex);
}

export function TenantConfigProvider({ children }: { children: ReactNode }) {
  const tenantId = readInitialTenantId();
  const [status, setStatus] = useState<TenantStatus>({ kind: 'loading', tenantId });

  useEffect(() => {
    const controller = new AbortController();
    setStatus({ kind: 'loading', tenantId });

    loadTenantConfig(tenantId, controller.signal)
      .then((config) => {
        applyBrandingToDocument(config);
        setStatus({ kind: 'ready', config });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        const message = err instanceof Error ? err.message : String(err);
        setStatus({ kind: 'error', tenantId, message });
      });

    return () => controller.abort();
  }, [tenantId]);

  const switchTenant = useCallback((id: string) => {
    // Switch tenant disabled under single-tenant blueprint
    void id;
  }, []);

  const value = useMemo<TenantConfigContextValue>(
    () => ({ status, switchTenant }),
    [status, switchTenant],
  );

  return <TenantConfigContext.Provider value={value}>{children}</TenantConfigContext.Provider>;
}
